import React, { useMemo } from "react"
import { useSelector } from "react-redux"
// eslint-disable-next-line no-restricted-imports -- FIXME: Usages of styled-components should be replaced with components from carotene-core or carotene-core-x. Please do not import styled-components after eslint rule added on 3/21/2025.
import styled from "styled-components"
import { useCurrentUser } from "../../context/user/UserContext"
import { DbgPaths } from "#/utils/Paths"
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl"
import { getCurrencyCode } from "#/redux/reducers/companyInfo"
import { getCategoryName } from "../reimbursements/ReimbursementCategoryMessages"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  DownIcon,
  InternalIcon,
  Link,
  Typography,
  IconWithBackground,
  MoneyIcon,
  Tile,
  Stack,
  useTheme,
  SurfaceColor
} from "@carrotfertility/carotene-core"
import { PlanInfoBadgeContainer } from "../../containers/planinfo/PlanInfoBadgeContainer"
import { getCategoryCoverage } from "#/utils/CategoryCoverage"
import { Subsidy } from "#/lib/carrot-api/types/Subsidy"

const optionsKey = "available-funds-module"
const availableFundsOptions = JSON.parse(localStorage.getItem(optionsKey)) || { expanded: {} }

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const CategoryExpands = styled.div`
  display: flex;
  flex-direction: row;
  margin: 1%;
  inline-size: 90%;
  justify-content: space-between;
`

const CategoryBenefitContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`

const AvailableFundsAmounts = ({
  subsidy,
  showRemainingAnnual,
  showRemainingLifetime
}: {
  subsidy: Subsidy
  showRemainingAnnual: boolean
  showRemainingLifetime: boolean
}) => {
  const lifetimeAvailable = subsidy.lifetimeAmounts.available
  const yearlyAvailable = subsidy.yearlyAmounts.available
  const hasUsedFunds = subsidy.lifetimeAmounts.claimed > 0
  const currencyCode = useSelector(getCurrencyCode)
  return (
    <CategoryExpands>
      {showRemainingAnnual && (
        <CategoryBenefitContainer>
          <div className={"flex flex-row flex-wrap"}>
            <Typography
              variant="overline"
              fontWeight="normal"
              color={(theme) => theme.palette.text.primary}
              marginRight={(theme) => theme.tokens.spacing.xxxl}
            >
              {hasUsedFunds ? (
                <FormattedMessage defaultMessage="Remaining annual" />
              ) : (
                <FormattedMessage defaultMessage="Available annual" />
              )}
            </Typography>
          </div>
          <Typography variant="h6" fontWeight="bold" color={(theme) => theme.palette.text.primary}>
            {subsidy.hasUnlimitedBenefit ? (
              <FormattedMessage defaultMessage="Unlimited" />
            ) : (
              <FormattedNumber
                value={Math.max(yearlyAvailable / 100, 0)}
                // eslint-disable-next-line react/style-prop-object -- "currency" is a valid style prop for FormattedNumber
                style="currency"
                currency={currencyCode}
                maximumFractionDigits={0}
                currencyDisplay="narrowSymbol"
              />
            )}
          </Typography>
        </CategoryBenefitContainer>
      )}
      {showRemainingLifetime && (
        <CategoryBenefitContainer>
          <div className={"flex flex-row flex-wrap"}>
            <Typography variant="overline" fontWeight="normal" color={(theme) => theme.palette.text.primary}>
              {hasUsedFunds ? (
                <FormattedMessage defaultMessage="Remaining lifetime" />
              ) : (
                <FormattedMessage defaultMessage="Available lifetime" />
              )}
            </Typography>
          </div>
          <Typography variant="h6" fontWeight="bold" color={(theme) => theme.palette.text.primary}>
            {subsidy.hasUnlimitedBenefit || subsidy.lifetimeAmounts.max === null ? (
              <FormattedMessage defaultMessage="Unlimited" />
            ) : (
              <FormattedNumber
                value={Math.max(lifetimeAvailable / 100, 0)}
                // eslint-disable-next-line react/style-prop-object -- "currency" is a valid style prop for FormattedNumber
                style="currency"
                currency={currencyCode}
                maximumFractionDigits={0}
                currencyDisplay="narrowSymbol"
              />
            )}
          </Typography>
        </CategoryBenefitContainer>
      )}
    </CategoryExpands>
  )
}

const AvailableFundsByCategory = ({
  subsidy,
  hasQmeCategory,
  showRemainingAnnual,
  showRemainingLifetime
}: {
  subsidy: Subsidy
  hasQmeCategory: boolean
  showRemainingAnnual: boolean
  showRemainingLifetime: boolean
}): JSX.Element => {
  const { employeeId } = useCurrentUser()
  const intl = useIntl()
  // eslint-disable-next-line no-prototype-builtins
  if (!availableFundsOptions.expanded.hasOwnProperty(employeeId)) {
    availableFundsOptions.expanded[employeeId] = {}
  }
  const categoryName = intl.formatMessage(getCategoryName(subsidy.reimbursementCategoryNameMessageKey, hasQmeCategory))
  return (
    <>
      <Box>
        <Accordion>
          <AccordionSummary
            expandIcon={<DownIcon fontSize="large" />}
            aria-controls={`accordion-content-available-funds-${categoryName}`}
            id={`accordion-content-available-funds-${categoryName}`}
            sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}
          >
            <Typography variant="h5">{categoryName}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
            <Typography sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
              <AvailableFundsAmounts
                subsidy={subsidy}
                showRemainingAnnual={showRemainingAnnual}
                showRemainingLifetime={showRemainingLifetime}
              />
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Divider />
    </>
  )
}

const AvailableFundsModuleCategories = ({ subsidies }: { subsidies: Subsidy[] }): JSX.Element => {
  const hasMultipleCategories = subsidies.length > 1
  const subsidiesToRender = useMemo(
    () =>
      subsidies.filter(
        (subsidy) =>
          !!subsidy.reimbursementCategoryNameMessageKey &&
          (subsidy.reimbursementCategoryNameMessageKey !== "OTHER" || subsidy.lifetimeAmounts.max !== 0)
      ),
    [subsidies]
  )
  const hasQmeCategory = getCategoryCoverage(subsidies).qme
  const showRemainingAnnual = subsidies.some((subsidy) => subsidy.yearlyAmounts.max !== subsidy.lifetimeAmounts.max)
  const showRemainingLifetime = subsidies.some((subsidy) => subsidy.lifetimeAmounts.max !== null)
  return (
    <>
      <CategoriesContainer>
        {subsidiesToRender.map((subsidy) =>
          hasMultipleCategories ? (
            <React.Fragment key={subsidy.reimbursementCategoryNameMessageKey}>
              <AvailableFundsByCategory
                subsidy={subsidy}
                hasQmeCategory={hasQmeCategory}
                showRemainingAnnual={showRemainingAnnual}
                showRemainingLifetime={showRemainingLifetime}
              />
            </React.Fragment>
          ) : (
            <React.Fragment key={subsidy.reimbursementCategoryNameMessageKey}>
              <AvailableFundsAmounts
                subsidy={subsidy}
                showRemainingAnnual={showRemainingAnnual}
                showRemainingLifetime={showRemainingLifetime}
              />
              <PlanInfoBadgeContainer subsidy={subsidy} allSubsidies={subsidiesToRender} stackVertically={true} />
            </React.Fragment>
          )
        )}
      </CategoriesContainer>
    </>
  )
}

const AvailableFundsModule = ({
  subsidies,
  hasMultipleCategories,
  color = "green"
}: {
  subsidies: Subsidy[]
  hasMultipleCategories: boolean
  color?: SurfaceColor
}): JSX.Element => {
  const theme = useTheme()
  return (
    <Tile variant="secondary" color={color}>
      <Stack
        position="relative"
        sx={{ overflow: "hidden" }}
        id="home-sidebar-available-benefit-module"
        spacing={theme.spacing(theme.tokens.spacing.lg)}
      >
        <Typography variant="h5">
          <FormattedMessage defaultMessage="Available funds" />
        </Typography>
        <Typography variant="body1" color={(theme) => theme.palette.text.secondary}>
          {hasMultipleCategories ? (
            <FormattedMessage defaultMessage="Your benefit includes funds to cover eligible expenses in the following categories." />
          ) : (
            <FormattedMessage defaultMessage="Your benefit includes funds to cover eligible expenses." />
          )}
        </Typography>
        {subsidies && <AvailableFundsModuleCategories subsidies={subsidies} />}
        <Link
          href={DbgPaths.CARROT_FUNDS}
          startIcon={<IconWithBackground icon={MoneyIcon} color={color} />}
          endIcon={<InternalIcon fontSize="small" color="primary" />}
        >
          <FormattedMessage defaultMessage="Learn about taxes, reimbursement, and paying for care" />
        </Link>
      </Stack>
    </Tile>
  )
}

export default AvailableFundsModule
